/** @format */

import GalleryBackIcon from '@/assets/joivy_arrow_left_miniature.svg';
import { GTMPDPRoomGalleryImageClick } from '@/ducks/googleTagManager/helpers/google-tag-manager';
import { TRoom } from '@/ducks/room/declarations';
import { FormattedMessage } from 'gatsby-plugin-intl';
import React, { memo, useState } from 'react';
import styled from 'styled-components';
import useModal from '../../../../shared/Modal';
import { Container } from '../../index.style';
import Lightbox from './Lightbox';

type mosaicPicturesForGallery = (
  | {
      index: number;
      url: string;
      type: string;
      poster: any;
      alt?: undefined;
      thumbnail?: undefined;
      representative?: undefined;
    }
  | {
      index: number;
      url: string;
      alt: string;
      thumbnail: string;
      representative: boolean;
      type?: undefined;
      poster?: undefined;
    }
  | undefined
)[][];

type picturesForGallery = (
  | {
      index: number;
      url: string;
      type: string;
      poster: any;
      alt?: undefined;
      thumbnail?: undefined;
      representative?: undefined;
    }
  | {
      index: number;
      url: string;
      alt: string;
      thumbnail: string;
      representative: boolean;
      type?: undefined;
      poster?: undefined;
    }
  | undefined
)[];

interface GalleryModalProps {
  showInsideGallery: boolean;
  setShowInsideGallery: React.Dispatch<React.SetStateAction<boolean>>;
  mosaicPicturesForGallery: mosaicPicturesForGallery;
  picturesForGallery: picturesForGallery;
  room: TRoom | undefined;
  formattedPrice: number;
}

const GalleryModal: React.FC<GalleryModalProps> = ({
  showInsideGallery,
  setShowInsideGallery,
  mosaicPicturesForGallery,
  picturesForGallery,
  room,
  formattedPrice,
}) => {
  const { Modal } = useModal();
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  const handleGTMPDPRoomGalleryImageClick = (index: number) => {
    if (room) {
      GTMPDPRoomGalleryImageClick(room.code, formattedPrice, index);
    }
  };

  return (
    <>
      <Modal
        isShow={showInsideGallery}
        background="255,255,255"
        opacity={1}
        onClose={() => setShowInsideGallery(false)}
      >
        <ModalGalleryContainer>
          <ModalGalleryContainerBackButtonContainer>
            <ModalGalleryContainerBackButton onClick={() => setShowInsideGallery(false)} type="button">
              <img src={GalleryBackIcon} alt="Gallery back icon" />
              <FormattedMessage id="back_to_room" />
            </ModalGalleryContainerBackButton>
          </ModalGalleryContainerBackButtonContainer>
          <ModalPhotosContainer className="gallery-container">
            <PhotosWrapper>
              {mosaicPicturesForGallery &&
                mosaicPicturesForGallery.map((photo, index) => (
                  <DoublePhotosContainer key={index} className={`photo ${photo && photo[1] ? 'photo-double' : ''}`}>
                    {photo &&
                      photo.map(
                        (singlePhoto, singlePhotoIndex) =>
                          singlePhoto &&
                          singlePhoto.url && (
                            <PhotoContainer
                              onClick={e => {
                                handleGTMPDPRoomGalleryImageClick(singlePhoto.index + 1);
                                setSelectedImageIndex(singlePhoto.index + 1);
                              }}
                              key={index + singlePhotoIndex}
                            >
                              <img
                                src={singlePhoto.url}
                                alt={singlePhoto.alt}
                                data-src={singlePhoto.url}
                                loading="lazy"
                              />
                            </PhotoContainer>
                          ),
                      )}
                  </DoublePhotosContainer>
                ))}
            </PhotosWrapper>
          </ModalPhotosContainer>
        </ModalGalleryContainer>
        {selectedImageIndex !== null && picturesForGallery && (
          <Lightbox
            gtmInfo={{
              monthlyRate: formattedPrice,
              roomCode: room?.code ?? '',
            }}
            photos={picturesForGallery}
            selectedImageIndex={selectedImageIndex - 1}
            setSelectedImageIndex={e => setSelectedImageIndex(e)}
          />
        )}
      </Modal>
    </>
  );
};

export default memo(GalleryModal);

const DoublePhotosContainer = styled.div``;

const PhotoContainer = styled.div`
  height: 100%;
  width: 100%;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  &:hover::after {
    content: '';
    width: 100%;
    height: 100%;
    left: 0;
    position: absolute;
    z-index: 2;
    content: ' ';
    width: 100%;
    height: 100%;
    top: 0;
    background-color: rgba(0, 0, 0, 0.1);
    transition: background-color 350ms ease-in-out;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
`;

const PhotosWrapper = styled.div`
  max-width: 878px;
  margin: 0 auto;

  display: grid;
  grid-gap: 20px;

  @media screen and (max-width: ${props => props.theme.breakpoints.xs}) {
    grid-gap: 10px;
  }

  grid-template-columns: 1fr;
  & > div.photo {
    max-height: 477px;
  }
  & > div.photo-double {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    @media screen and (max-width: ${props => props.theme.breakpoints.xs}) {
      gap: 10px;
    }
    max-height: 317px;
  }
  padding-bottom: 200px;
`;

const ModalGalleryContainerBackButtonContainer = styled(Container)`
  padding-bottom: 30px;

  @media screen and (max-width: ${props => props.theme.breakpoints.xs}) {
    padding-bottom: 35px;
    padding-top: 35px;
  }
`;

const ModalGalleryContainerBackButton = styled.button`
  cursor: pointer;
  padding: 5px;

  font-size: 1.125rem;
  line-height: 1.17;
  color: ${({ theme }) => theme.colors.joivy['teal-dark']};
  background: none;
  border: none;
  margin-top: 1.8em;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  transition: all 0.3s ease-in-out 0s;

  &:hover {
    color: ${({ theme }) => theme.colors.joivy['teal-light']};
  }

  & > img {
    display: inline-block;
    width: 28px;
    height: 27px;
  }
`;

const ModalPhotosContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding-bottom: 50px;
`;

const ModalGalleryContainer = styled.div`
  height: 100%;
`;
